import { useCallback } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Link from "ds/components/Link";
import { showOpenTofuMigrationClearQueueConfirmationModal } from "shared/Stack/OpenTofuMigration/ClearQueueConfirmationModal";
import OpenTofuMigrationStateBadge from "shared/Stack/OpenTofuMigration/StateBadge";
import useOpenTofuMigrationSearchQueue from "shared/Stack/OpenTofuMigration/useSearchQueue";
import { OpenTofuMigrationState } from "types/generated";
import { OPEN_TOFU_MIGRATION_QUEUE_PATH } from "views/Account/OpenTofuMigration/constants";

import {
  DASHBOARD_WIDGETS_OPEN_TOFU_MIGRATION_CLEAR_QUEUE_REFETCH_QUERIES,
  DASHBOARD_WIDGETS_OPEN_TOFU_MIGRATION_QUEUE_VARIABLES,
} from "../constants";
import DashboardWidgetsOpenTofuMigrationStackList from "./StackList";

type DashboardWidgetsOpenTofuMigrationQueueListProps = {
  queuedToMigrate: number;
};

const DashboardWidgetsOpenTofuMigrationQueueList = ({
  queuedToMigrate,
}: DashboardWidgetsOpenTofuMigrationQueueListProps) => {
  const { entities, isLoading, error, refetch } = useOpenTofuMigrationSearchQueue({
    variables: DASHBOARD_WIDGETS_OPEN_TOFU_MIGRATION_QUEUE_VARIABLES,
    pollingGroup: PollingIntervalGroups.Dashboard,
  });

  const handleClearQueue = useCallback(
    () =>
      showOpenTofuMigrationClearQueueConfirmationModal({
        refetchQueries: DASHBOARD_WIDGETS_OPEN_TOFU_MIGRATION_CLEAR_QUEUE_REFETCH_QUERIES,
      }),
    []
  );

  return (
    <DashboardWidgetsOpenTofuMigrationStackList
      title="Queued"
      action={
        <Box align="center" gap="medium">
          {queuedToMigrate > 0 && (
            <Button variant="secondary" size="small" onPress={handleClearQueue}>
              Clear pending entries
            </Button>
          )}
          <Link href={OPEN_TOFU_MIGRATION_QUEUE_PATH}>See all</Link>
        </Box>
      }
      badge={<OpenTofuMigrationStateBadge state={OpenTofuMigrationState.ToMigrate} />}
      stacks={entities.map((entry) => entry.stack)}
      isLoading={isLoading}
      hasError={!!error}
      errorText="Couldn’t load the migration queue. Please try to refresh or come back later."
      emptyTitle="Nothing queued yet"
      emptyCaption="Stacks queued for migration will show up here."
      onRefetch={refetch}
    />
  );
};

export default DashboardWidgetsOpenTofuMigrationQueueList;
